import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Toast, Modal, Form, Select, Typography, Popover, Steps } from '@douyinfe/semi-ui';
import { IconSearch, IconPlus, IconCopy, IconLink } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import { copyToClipboard } from '../../utils/clipboard';
import { QRCodeCanvas } from 'qrcode.react';

const statusMap = {
    task_created: { text: '创建中', type: 'warning' },
    finish_creation: { text: '已创建', type: 'default' },
    fill_progress: { text: '填写中', type: 'primary' },
    fill_completed: { text: '填写已完成', type: 'default' },
    sign_progress: { text: '签署中', type: 'primary' },
    sign_completed: { text: '签署已完成', type: 'success' },
    task_finished: { text: '已完成', type: 'success' },
    task_terminated: { text: '停止', type: 'danger' },
    expired: { text: '已逾期', type: 'danger' },
    abolishing: { text: '作废中', type: 'warning' },
    revoked: { text: '已作废', type: 'danger' },
};

// key到中文的映射
const fddTplInfoKeyMap = {
    signTaskId: '签署任务ID',
    storageType: '存储类型',
    signTaskSource: '任务来源',
    signTaskSubject: '签署主题',
    //catalogId: '目录ID',
    catalogName: '目录名称',
    //templateId: '模板ID',
    signTaskStatus: '任务状态',
    initiatorName: '发起方',
    //createTime: '创建时间',
    startTime: '开始时间',
    finishTime: '完成时间',
    actorResults: '签署方结果',
    actorName: '签署方名称',
    actorId: '签署方ID',
};

const FddTasks = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [tpls, setTpls] = useState([]);
    const [createVisible, setCreateVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createForm, setCreateForm] = useState({ tpl_uid: '', name: '' });
    const [actorPopover, setActorPopover] = useState({ visible: false, task_uid: '', actors: [] });
    // 新增：签署链接弹窗相关state
    const [signLinkModal, setSignLinkModal] = useState({
        visible: false,
        task_uid: '',
        actors: [],
        selectedActorUid: '',
        url: '',
        actorName: ''
    });
    // 新增：预填二维码弹窗相关state
    const [prefillModal, setPrefillModal] = useState({ visible: false, url: '' });

    // 获取任务列表
    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/contract/fdd/list_task/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取任务列表失败');
            }
        } catch (error) {
            Toast.error('获取任务列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 获取模板列表（用于创建任务）
    const fetchTpls = async () => {
        try {
            const response = await request.get('/go/sp/contract/fdd/list_tpl/', { params: { page: 0, limit: 100 } });
            if (response.errno === 0) {
                setTpls(response.list || []);
            }
        } catch { }
    };

    useEffect(() => {
        fetchList();
    }, []);

    // 创建任务
    const handleCreate = async () => {
        if (!createForm.tpl_uid || !createForm.name) {
            Toast.warning('请选择模板并填写任务名称');
            return;
        }
        setCreateLoading(true);
        try {
            const response = await request.post('/go/sp/contract/fdd/create_task/', createForm);
            if (response.errno === 0) {
                Toast.success('创建成功');
                setCreateVisible(false);
                setCreateForm({ tpl_uid: '', name: '' });
                fetchList(1, pagination.pageSize);
            } else {
                Toast.error(response.errstr || '创建失败');
            }
        } catch {
            Toast.error('创建失败');
        } finally {
            setCreateLoading(false);
        }
    };

    // 获取预填URL
    const handlePrefill = async (task_uid) => {
        try {
            const response = await request.get('/go/sp/contract/fdd/get_task_prefill_url/', { params: { task_uid } });
            if (response.errno === 0) {
                setPrefillModal({ visible: true, url: response.url });
            } else {
                Toast.error(response.errstr || '获取预填链接失败');
            }
        } catch {
            Toast.error('获取预填链接失败');
        }
    };

    // 开始任务
    const handleStart = async (task_uid) => {
        try {
            const response = await request.post('/go/sp/contract/fdd/start_task/', { task_uid });
            if (response.errno === 0) {
                Toast.success('任务已开始');
                fetchList(pagination.currentPage, pagination.pageSize);
            } else {
                Toast.error(response.errstr || '开始任务失败');
            }
        } catch {
            Toast.error('开始任务失败');
        }
    };

    // 获取签署方列表并弹出
    const handleShowActors = (record) => {
        // 假设后端返回有 actors 字段，若无可调整
        setActorPopover({ visible: true, task_uid: record.uid, actors: record.actors || [] });
    };

    // 获取签署方签署URL
    const handleActorSign = async (task_uid, actor_uid, actorName) => {
        try {
            const response = await request.get('/go/sp/contract/fdd/get_task_actor_url/', { params: { task_uid, actor_uid } });
            if (response.errno === 0) {
                setSignLinkModal({ visible: true, url: response.url, actorName: actorName || '' });
            } else {
                Toast.error(response.errstr || '获取签署链接失败');
            }
        } catch {
            Toast.error('获取签署链接失败');
        }
    };

    // 下载合同
    const handleDownload = async (task_uid) => {
        try {
            const response = await request.get('/go/sp/contract/fdd/get_task_download_url/', { params: { task_uid } });
            if (response.errno === 0 && response.url) {
                window.open(response.url, '_blank');
            } else {
                Toast.error(response.errstr || '获取下载链接失败');
            }
        } catch {
            Toast.error('获取下载链接失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        { title: '任务名称', dataIndex: 'name', width: 200 },
        {
            title: '状态', dataIndex: 'status', width: 120, render: (text) => {
                const status = statusMap[text] || { text, type: 'default' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建/更新时间', dataIndex: 'update_time', width: 180, render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '详情',
            dataIndex: 'fdd_tpl_info',
            width: 320,
            render: (text) => {
                let info = {};
                try {
                    info = JSON.parse(text || '{}');
                } catch { }
                return (
                    <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 180, overflow: 'auto' }}>
                        {Object.keys(info).length === 0 ? '无' : Object.entries(info).map(([k, v]) => {
                            const label = fddTplInfoKeyMap[k];
                            if (!label) return;
                            if (Array.isArray(v)) {
                                // 针对 actorResults 特殊处理
                                if (k === 'actorResults') {
                                    return (
                                        <div key={k} style={{ marginBottom: 4 }}>
                                            <b>{label}：</b>
                                            <div style={{ marginLeft: 12 }}>
                                                {v.map((actor, idx) => (
                                                    <div key={idx}>
                                                        {fddTplInfoKeyMap['actorName'] || 'actorName'}：{actor.actorName || '-'}，{fddTplInfoKeyMap['actorId'] || 'actorId'}：{actor.actorId || '-'}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                } else {
                                    return <div key={k}><b>{label}：</b>{JSON.stringify(v)}</div>;
                                }
                            } else if (typeof v === 'object' && v !== null) {
                                return <div key={k}><b>{label}：</b>{JSON.stringify(v)}</div>;
                            } else if (k.endsWith('Time') && v && !isNaN(Number(v))) {
                                // 时间戳转中文
                                const date = new Date(Number(v));
                                return <div key={k}><b>{label}：</b>{date.toLocaleString()}</div>;
                            } else {
                                return <div key={k}><b>{label}：</b>{String(v)}</div>;
                            }
                        })}
                    </div>
                );
            }
        },
        {
            title: '操作',
            dataIndex: 'op',
            width: 260,
            render: (_, record) => {
                const btns = [];
                // 预填：仅在 task_created/finish_creation
                if (['task_created', 'finish_creation'].includes(record.status)) {
                    btns.push(<Button size="small" onClick={() => handlePrefill(record.uid)} key="prefill">预填</Button>);
                }
                // 开始：
                if (['task_created', 'finish_creation'].includes(record.status)) {
                    btns.push(<Button size="small" onClick={() => handleStart(record.uid)} key="start">开始</Button>);
                }
                // 签署：改为弹出Modal选择actor
                if (["sign_progress", "fill_progress"].includes(record.status)) {
                    btns.push(
                        <Button
                            size="small"
                            onClick={() => {
                                let actors = [];
                                try {
                                    const info = JSON.parse(record.fdd_tpl_info || '{}');
                                    if (Array.isArray(info.actorResults)) {
                                        actors = info.actorResults.map(a => ({
                                            uid: a.actorId,
                                            name: a.actorName
                                        }));
                                    }
                                } catch { }
                                // 兜底用 record.actors
                                if (!actors.length && Array.isArray(record.actors)) {
                                    actors = record.actors;
                                }
                                setSignLinkModal({
                                    visible: true,
                                    task_uid: record.uid,
                                    actors,
                                    selectedActorUid: '',
                                    url: '',
                                    actorName: ''
                                });
                            }}
                            key="sign"
                        >
                            获取签署链接
                        </Button>
                    );
                }
                // 签署已完成/任务已完成/已作废等，仅提示，并增加下载按钮
                if (["sign_completed", "task_finished", "revoked", "expired", "task_terminated"].includes(record.status)) {
                    if (["sign_completed", "task_finished"].includes(record.status)) {
                        btns.push(<Button size="small" onClick={() => handleDownload(record.uid)} key="download">下载合同</Button>);
                    }
                }
                return <Space>{btns}</Space>;
            }
        }
    ];

    return (
        <div style={{ padding: 20 }}>
            {/* 合同签署流程说明 */}
            <div style={{ marginBottom: 24 }}>
                <Typography.Title heading={5}>合同签署流程说明</Typography.Title>
                <Steps current={-1} style={{ maxWidth: 1200 }}>
                    <Steps.Step title="补充预填信息" description="补充合同预填信息" />
                    <Steps.Step title="开始签署" description="发起合同签署流程" />
                    <Steps.Step title="获取各方签署链接" description="为每个签署方获取专属签署链接并分发" />
                    <Steps.Step title="等待各方签署完毕" description="等待所有签署方完成签署" />
                    <Steps.Step title="签署完毕" description="合同签署流程结束" />
                </Steps>
            </div>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Input
                        placeholder="搜索任务名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button
                        type="primary"
                        icon={<IconPlus />}
                        onClick={() => { setCreateVisible(true); fetchTpls(); }}
                    >
                        新建签署
                    </Button>
                </Space>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                scroll={{ x: 1000 }}
            />
            <Modal
                title="创建签署任务"
                visible={createVisible}
                onCancel={() => setCreateVisible(false)}
                onOk={handleCreate}
                confirmLoading={createLoading}
            >
                <Form layout="vertical">
                    <Form.Select
                        field="tpl_uid"
                        label="选择合同模板"
                        value={createForm.tpl_uid}
                        onChange={v => setCreateForm(f => ({ ...f, tpl_uid: v }))}
                        placeholder="请选择合同模板"
                        style={{ width: '100%' }}
                        required
                    >
                        {tpls.map(tpl => (
                            <Select.Option value={tpl.uid} key={tpl.uid}>{tpl.name}</Select.Option>
                        ))}
                    </Form.Select>
                    <Form.Input
                        field="name"
                        label="任务名称"
                        value={createForm.name}
                        onChange={v => setCreateForm(f => ({ ...f, name: v }))}
                        placeholder="请输入任务名称"
                        required
                    />
                </Form>
            </Modal>
            <Modal
                title={signLinkModal.actorName ? `签署链接 - ${signLinkModal.actorName}` : '获取签署链接'}
                visible={signLinkModal.visible}
                onCancel={() => setSignLinkModal({ ...signLinkModal, visible: false })}
                footer={null}
            >
                <div style={{ marginBottom: 16 }}>
                    <Select
                        placeholder="请选择签署方"
                        value={signLinkModal.selectedActorUid}
                        onChange={v => setSignLinkModal(modal => ({
                            ...modal,
                            selectedActorUid: v,
                            actorName: (modal.actors.find(a => a.uid === v) || {}).name || ''
                        }))}
                        style={{ width: '100%' }}
                    >
                        {signLinkModal.actors.map(actor => (
                            <Select.Option value={actor.uid} key={actor.uid}>
                                {actor.name || actor.uid}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <Button
                    type="primary"
                    onClick={async () => {
                        if (!signLinkModal.selectedActorUid) {
                            Toast.warning('请选择签署方');
                            return;
                        }
                        try {
                            const response = await request.get('/go/sp/contract/fdd/get_task_actor_url/', {
                                params: {
                                    task_uid: signLinkModal.task_uid,
                                    actor_uid: signLinkModal.selectedActorUid
                                }
                            });
                            if (response.errno === 0) {
                                setSignLinkModal(modal => ({
                                    ...modal,
                                    url: response.url
                                }));
                            } else {
                                Toast.error(response.errstr || '获取签署链接失败');
                            }
                        } catch {
                            Toast.error('获取签署链接失败');
                        }
                    }}
                    style={{ marginBottom: 16 }}
                >
                    生成链接
                </Button>
                <div style={{ wordBreak: 'break-all', marginBottom: 16 }}>
                    {signLinkModal.url || '-'}
                </div>
                {/* 二维码展示 */}
                {signLinkModal.url && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <QRCodeCanvas value={signLinkModal.url} size={160} />
                        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>手机扫码访问签署链接</div>
                    </div>
                )}
                <Space>
                    <Button
                        onClick={() => {
                            if (signLinkModal.url) {
                                copyToClipboard(signLinkModal.url)
                                    .then(() => {
                                        Toast.success('已复制链接');
                                    })
                                    .catch(() => {
                                        Toast.error('复制失败');
                                    });
                            }
                        }}
                        type="primary"
                        disabled={!signLinkModal.url}
                    >
                        复制链接
                    </Button>
                    <Button
                        onClick={() => {
                            if (signLinkModal.url) window.open(signLinkModal.url, '_blank');
                        }}
                        disabled={!signLinkModal.url}
                    >
                        打开链接
                    </Button>
                </Space>
            </Modal>
            {/* 预填二维码弹窗 */}
            <Modal
                title="预填链接二维码"
                visible={prefillModal.visible}
                onCancel={() => setPrefillModal({ visible: false, url: '' })}
                footer={null}
            >
                <div style={{ wordBreak: 'break-all', marginBottom: 16 }}>
                    {prefillModal.url || '-'}
                </div>
                {prefillModal.url && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <QRCodeCanvas value={prefillModal.url} size={160} />
                        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>手机扫码访问预填链接</div>
                    </div>
                )}
                <Space>
                    <Button
                        onClick={() => {
                            if (prefillModal.url) {
                                copyToClipboard(prefillModal.url)
                                    .then(() => {
                                        Toast.success('已复制链接');
                                    })
                                    .catch(() => {
                                        Toast.error('复制失败');
                                    });
                            }
                        }}
                        type="primary"
                        disabled={!prefillModal.url}
                    >
                        复制链接
                    </Button>
                    <Button
                        onClick={() => {
                            if (prefillModal.url) window.open(prefillModal.url, '_blank');
                        }}
                        disabled={!prefillModal.url}
                    >
                        打开链接
                    </Button>
                </Space>
            </Modal>
        </div>
    );
};

export default FddTasks; 